import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from "styled-components";
import Colours from "../../atoms/Colours";
import { BodyRegular, BodySmall, BodyVerySmall } from "../../atoms/fonts/Body";
import { Initials } from "../../molecules/UserAvatarAndName";
import { OutlineButton } from "../../atoms/buttons/OutlineButton";
import { agent } from '@shared/services/agent';
import { SimpleSpinner } from "../../atoms/SimpleSpinner";
import { Center } from "../../atoms/StructuralLayout";
import Icon, { IconTypes } from "../../atoms/icon/Icon";
import { BasicUserInfo, Permission } from '@shared/models/user.models';
import { useDispatch, useSelector } from "react-redux";
import { redirectTo, selectCurrentUser, selectIsInTeams } from '@shared/store/auth.duck';
import { favouriteUserRequest } from '@features/whereabouts/services/favourite-user';
import IconButton from "../../molecules/icon-button/IconButton";
import { HolidayUsagePopup } from "@features/holidays/pages/components/HolidayUsagePopup";
import { deepLinkTo } from '@shared/utils/TeamsUtils';
import { teamsTabs } from '@shared/services/EnvironmentVariables';
import { selectCalendarDate } from '@shared/store/dashboard.duck';
import { DATE_FORMAT } from '@shared/utils/DateUtils';
import { loadFavouritedUsersWhereabouts } from '@features/whereabouts/store/FavouritedUsersWhereabouts.duck';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { canViewOtherUsersLeave } from '@shared/utils/AccessControl';

export interface UserData {
  officeLabel?: string;
  favourite: boolean;
  jobTitle?: string;
}

export function UserAvatarInfoHoverBox(props: Props) {
  const {firstName, lastName, jobTitle, initials} = props;
  const [userData, setUserData] = useState<UserData | undefined>();
  const [loading, setLoading] = useState(false);
  const name = firstName + ' ' + lastName;
  const currentUser = useSelector(selectCurrentUser);
  const dispatch = useDispatch();
  const calendarDate = useSelector(selectCalendarDate);
  const isInTeams = useSelector(selectIsInTeams);
  const showUserSettings = (currentUser?.role === Permission.CompanyAdmin || currentUser?.role === Permission.TeamAdmin || currentUser?.role === Permission.DepartmentAdmin);
  const canViewLeave = canViewOtherUsersLeave(currentUser);

  useEffect(() => {
    setLoading(true);
    let id = setTimeout(() => {
      if (props.userId) {
        agent.requests.get(`/api/user/info/${props.userId}`)
          .then((result: UserData) => setUserData(result))
          .finally(() => setLoading(false))
      }
    }, 300);

    return () => {
      clearTimeout(id);
    }
  }, [props.userId]);

  const addToFavourites = async (add: boolean) => {
    if (currentUser?.id) {
      try {
        setLoading(true);
        await favouriteUserRequest(currentUser.id, props.userId, add);
        dispatch(loadFavouritedUsersWhereabouts({date: calendarDate.format(DATE_FORMAT)}));
        setUserData({...userData, favourite: add});
      } finally {
        setLoading(false);
      }
    }
  }

  const onViewLeaveClicked = () => {
    if (isInTeams) {
      deepLinkTo(teamsTabs.holidays, `/holidays/${props.userId}`, 'leave', props.userId);
    } else {
      dispatch(redirectTo(`/holidays/${props.userId}`));
    }
  }

  const onSettingsClicked = () => {
    if (isInTeams) {
      deepLinkTo(teamsTabs.settings, `/manage/user/${props.userId}`, 'user', props.userId);
    } else {
      dispatch(redirectTo(`/manage/user/${props.userId}`));
    }
  }

  return ReactDOM.createPortal(
    <Container x={props.x} y={props.y}>
      <Header>
        <Initials className={`userAvatarAndName__avatar userAvatarAndName__avatar--darkBlue`.trim()}>{initials.toUpperCase()}</Initials>
        <Column>
          <BodyRegular className={!!jobTitle ? 'userAvatarAndName__name--jobTitle' : 'userAvatarAndName__jobTitle'}
                       style={{marginTop: !!jobTitle ? 14 : 0, marginLeft: 8}}
                       colour={Colours.white}
                       title={name}>{name}</BodyRegular>
          {userData?.jobTitle && <BodyVerySmall colour={Colours.white}
                                                style={{marginLeft: 8}}
                                                title={userData.jobTitle}
                                                className={`userAvatarAndName__jobTitle`}>{userData.jobTitle}</BodyVerySmall>}
        </Column>
        {showUserSettings && <IconButton icon={IconTypes.Settings}
                                         className={'userAvatar__settingsIcon'}
                                         onClick={onSettingsClicked}
                                         color={Colours.white} />}
      </Header>
      <Content>
        {props.showHolidayContentOnHover && props.basicUserInfo ? <>
          <HolidayUsagePopup user={props.basicUserInfo} />
          <Controls>
            <OutlineButton click={() => addToFavourites(!userData?.favourite)} text={userData?.favourite ? "user-hover.remove-favourites" : "user-hover.add-favourites"} disabled={loading} size={ButtonSize.Small} />
            <OutlineButton click={onViewLeaveClicked} text={"user-hover.leave"} disabled={!canViewLeave} size={ButtonSize.Small}/>
          </Controls>
        </> : <>
          {loading ? <>
            <Center>
              <SimpleSpinner size={16} />
            </Center>
          </> : <>
            {userData?.officeLabel && <Row>
              <Icon icon={IconTypes.Office} color={'grey'}/>
              <BodySmall>{userData.officeLabel}</BodySmall>
            </Row>}
          </>}
          <Controls>
            <OutlineButton click={() => addToFavourites(!userData?.favourite)}
                           text={userData?.favourite ? "user-hover.remove-favourites" : "user-hover.add-favourites"}
                           disabled={loading}
                           size={ButtonSize.Small} />
            <OutlineButton click={onViewLeaveClicked} text={"user-hover.leave"} disabled={!canViewLeave} size={ButtonSize.Small}/>
          </Controls>
        </>}
      </Content>
    </Container>,
    document.body,
  )
}

interface Props {
  x: number;
  y: number;
  firstName: string;
  lastName: string;
  jobTitle?: string;
  initials: string;
  userId: string;
  showHolidayContentOnHover: boolean;
  basicUserInfo?: BasicUserInfo;
}

const Column = styled.div`

`

const Row = styled.div`
  display: flex;
  align-items: center;
  i {
    margin-right: 8px;
  }
`

const Container = styled.div<any>`
  z-index: 99999999;
  position: fixed;
  min-width: 400px;
  float: left;
  min-height: 100px;
  background-color: ${Colours.white};
  box-shadow: 0 32px 30px 2px rgba(81, 91, 176, 0.19);
  overflow: hidden;
  border-radius: 12px;
   left: ${props => props.x}px;
   top: ${props => props.y}px;
`

const Header = styled.div`
  position: relative;
  padding: 16px;
  background-color: #515BB0;
  display: flex;
  .userAvatar__settingsIcon {
    position: absolute;
    right: 16px;
    top: 16px;
  }
`

const Content = styled.div`
  width: 100%;
  padding: 16px 16px 16px 50px;
`

const Controls = styled.div`
  display: flex;
  margin-top: 24px;
  button {
    width: unset;
    margin-right: 12px;
    white-space: nowrap;
  }
`
